import { cn } from '@/lib/utils';
import * as React from 'react';

import { FluidHoverHighlight } from '@/components/fluid-hover-highlight';

import {
  type UseFluidHoverReturn,
  useFluidHover,
  useRegisterFluidHoverItem
} from '@/hooks/use-fluid-hover';

type TableVariant = 'default' | 'fluid';
type TableSection = 'head' | 'body';

interface TableContextValue {
  variant: TableVariant;
  registerItem?: UseFluidHoverReturn['registerItem'];
  claimIndex?: () => number;
}

const TableContext = React.createContext<TableContextValue>({
  variant: 'default'
});
const TableSectionContext = React.createContext<TableSection>('body');

interface TableProps extends React.HTMLAttributes<HTMLTableElement> {
  containerClassName?: string;
  variant?: TableVariant;
}

const FluidTable = React.forwardRef<
  HTMLTableElement,
  Omit<TableProps, 'variant'>
>(({ className, containerClassName, ...props }, ref) => {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const hover = useFluidHover(containerRef, { gapClick: false });
  const nextIndex = React.useRef(0);

  const value = React.useMemo<TableContextValue>(
    () => ({
      variant: 'fluid',
      registerItem: hover.registerItem,
      claimIndex: () => nextIndex.current++
    }),
    [hover.registerItem]
  );

  return (
    <TableContext.Provider value={value}>
      <div
        ref={containerRef}
        className={cn('relative w-full overflow-auto', containerClassName)}
        onMouseEnter={hover.handlers.onMouseEnter}
        onMouseMove={hover.handlers.onMouseMove}
        onMouseLeave={hover.handlers.onMouseLeave}
      >
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          <FluidHoverHighlight hover={hover} className="rounded-lg" />
        </div>
        <table
          ref={ref}
          className={cn(
            'w-full text-sm caption-bottom border-collapse',
            className
          )}
          {...props}
        />
      </div>
    </TableContext.Provider>
  );
});
FluidTable.displayName = 'FluidTable';

const Table = React.forwardRef<HTMLTableElement, TableProps>(
  ({ className, containerClassName, variant = 'default', ...props }, ref) =>
    variant === 'fluid' ? (
      <FluidTable
        ref={ref}
        className={className}
        containerClassName={containerClassName}
        {...props}
      />
    ) : (
      <div className={cn('relative w-full overflow-auto', containerClassName)}>
        <table
          ref={ref}
          className={cn('w-full text-sm caption-bottom', className)}
          {...props}
        />
      </div>
    )
);
Table.displayName = 'Table';

const TableHeader = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => {
  const { variant } = React.useContext(TableContext);
  return (
    <TableSectionContext.Provider value="head">
      <thead
        ref={ref}
        className={cn(
          '[&_tr]:border-b',
          variant === 'fluid' &&
            '[&:has(+tbody>tr:first-child[data-fluid-hover-active])_tr]:border-transparent',
          className
        )}
        {...props}
      />
    </TableSectionContext.Provider>
  );
});
TableHeader.displayName = 'TableHeader';

const TableBody = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <TableSectionContext.Provider value="body">
    <tbody
      ref={ref}
      className={cn('[&_tr:last-child]:border-0', className)}
      {...props}
    />
  </TableSectionContext.Provider>
));
TableBody.displayName = 'TableBody';

const TableFooter = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tfoot
    ref={ref}
    className={cn(
      'bg-muted/50 border-t last:[&>tr]:border-b-0 font-medium',
      className
    )}
    {...props}
  />
));
TableFooter.displayName = 'TableFooter';

const TableRow = React.forwardRef<
  HTMLTableRowElement,
  React.HTMLAttributes<HTMLTableRowElement>
>(({ className, ...props }, forwardedRef) => {
  const { variant, registerItem, claimIndex } =
    React.useContext(TableContext);
  const section = React.useContext(TableSectionContext);
  const isFluid = variant === 'fluid';
  const isHoverable = isFluid && section === 'body';
  const rowRef = React.useRef<HTMLTableRowElement | null>(null);
  const [index] = React.useState(() =>
    isHoverable ? claimIndex?.() : undefined
  );
  useRegisterFluidHoverItem(isHoverable ? registerItem : undefined, index, rowRef);

  const setRefs = React.useCallback(
    (node: HTMLTableRowElement | null) => {
      rowRef.current = node;
      if (typeof forwardedRef === 'function') forwardedRef(node);
      else if (forwardedRef) forwardedRef.current = node;
    },
    [forwardedRef]
  );

  return (
    <tr
      ref={setRefs}
      className={cn(
        isFluid
          ? 'group/row relative z-10 data-[state=selected]:bg-muted border-border data-fluid-hover-active:border-transparent has-[+tr[data-fluid-hover-active]]:border-transparent border-b transition-[border-color] duration-80'
          : 'data-[state=selected]:bg-muted hover:bg-muted/50 border-b transition-colors',
        className
      )}
      {...props}
    />
  );
});
TableRow.displayName = 'TableRow';

const TableHead = React.forwardRef<
  HTMLTableCellElement,
  React.ThHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => {
  const { variant } = React.useContext(TableContext);
  return (
    <th
      ref={ref}
      className={cn(
        'text-left align-middle has-[[role=checkbox]]:pr-0',
        variant === 'fluid'
          ? 'px-3 py-2 font-medium text-foreground'
          : 'h-12 px-4 font-medium text-muted-foreground',
        className
      )}
      {...props}
    />
  );
});
TableHead.displayName = 'TableHead';

const TableCell = React.forwardRef<
  HTMLTableCellElement,
  React.TdHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => {
  const { variant } = React.useContext(TableContext);
  return (
    <td
      ref={ref}
      className={cn(
        'align-middle has-[[role=checkbox]]:pr-0',
        variant === 'fluid'
          ? 'px-3 py-2 text-muted-foreground transition-colors duration-80 group-data-fluid-hover-active/row:text-foreground'
          : 'p-4',
        className
      )}
      {...props}
    />
  );
});
TableCell.displayName = 'TableCell';

const TableCaption = React.forwardRef<
  HTMLTableCaptionElement,
  React.HTMLAttributes<HTMLTableCaptionElement>
>(({ className, ...props }, ref) => (
  <caption
    ref={ref}
    className={cn('mt-4 text-muted-foreground text-sm', className)}
    {...props}
  />
));
TableCaption.displayName = 'TableCaption';

export {
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableHead,
  TableRow,
  TableCell,
  TableCaption
};
